/**
 * DeploymentBanner.js
 * Fixed top-right notice shown on ALL screens (login + app) when a deployment is in progress.
 * Admin can toggle it on/off from the Admin Panel → Settings tab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 10_000; // re-check every 10 seconds

export default function DeploymentBanner() {
  const [notice, setNotice]       = useState({ active: false, message: '' });
  const [dismissed, setDismissed] = useState(false);

  const fetchNotice = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/system/deployment-notice`);
      setNotice(prev => {
        // Skip state update if nothing changed — avoids re-renders every 10s.
        if (prev.active === data.active && prev.message === data.message) return prev;
        // Re-show if admin turned it back on after user dismissed
        if (data.active && !prev.active) setDismissed(false);
        return data;
      });
    } catch {
      // Silently ignore
    }
  }, []);

  useEffect(() => {
    fetchNotice();
    // Pause polling while the tab is hidden — no point burning CPU/network
    // on a background tab.
    let id = null;
    const start = () => {
      if (id) return;
      id = setInterval(fetchNotice, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (id) { clearInterval(id); id = null; }
    };
    if (document.visibilityState === 'visible') start();
    const onVis = () => {
      if (document.visibilityState === 'visible') { fetchNotice(); start(); }
      else stop();
    };
    document.addEventListener('visibilitychange', onVis);

    // Instant update when admin enables/disables from same page (custom event)
    const onSignal = () => fetchNotice();
    window.addEventListener('deploymentNoticeChanged', onSignal);

    // Instant update when admin enables/disables from a different tab (localStorage)
    const onStorage = (e) => {
      if (e.key === 'deploymentNoticeSignal') fetchNotice();
    };
    window.addEventListener('storage', onStorage);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('deploymentNoticeChanged', onSignal);
      window.removeEventListener('storage', onStorage);
    };
  }, [fetchNotice]);

  if (!notice.active || dismissed) return null;

  return (
    <div
      data-testid="deployment-banner"
      className="fixed top-3 right-3 z-[9998] max-w-sm w-[calc(100vw-1.5rem)] sm:w-80
                 bg-amber-50 border border-amber-300 shadow-lg rounded-xl
                 px-4 py-3 flex items-start gap-3 animate-in slide-in-from-top-2 duration-300"
    >
      {/* Pulsing dot indicator */}
      <div className="flex-shrink-0 mt-0.5 relative">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-100">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
        </span>
        <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
        </span>
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-amber-900 leading-snug">Deployment in Progress</p>
        <p className="text-[11px] text-amber-700 mt-0.5 leading-relaxed">
          {notice.message || 'Performance may be temporarily affected. Please retry if something seems slow.'}
        </p>
      </div>

      {/* Dismiss */}
      <button
        onClick={() => setDismissed(true)}
        className="flex-shrink-0 text-amber-400 hover:text-amber-700 transition-colors mt-0.5"
        aria-label="Dismiss"
        data-testid="dismiss-deployment-banner"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
